import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  FacilityDto,
  FacilitySearchDto,
  FacilityService
} from '@umss/shared/data-access';
import {
  SelectLoading,
  SelectOption,
  UmssSelectComponent
} from '../select/select.component';
import { UmssVerticalFieldComponent } from '../vertical-field/vertical-field.component';

const PAGE_SIZE = 20;

@Component({
  selector: 'umss-facility-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent, UmssVerticalFieldComponent],
  template: `
    <umss-vertical-field
      [label]="label"
      [required]="required"
      [disabled]="disabled"
    >
      <umss-select
        [placeholder]="placeholder"
        [options]="options"
        [value]="value"
        [searchable]="searchable"
        [clearable]="clearable"
        [multiple]="multiple"
        [disabled]="disabled"
        [required]="required"
        [loading]="loading"
        [isPaginated]="true"
        (valueChange)="onValueChange($event)"
        (searchChange)="onSearchChange($event)"
        (onScroll)="onScrollOptions($event)"
      />
    </umss-vertical-field>
  `,
  styles: `
    :host {
      display: contents;
    }

    ::ng-deep .vertical-field {
      flex: 1;
      min-width: 0;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssFacilitySelectComponent),
      multi: true
    }
  ]
})
export class UmssFacilitySelectComponent
  implements ControlValueAccessor, OnInit
{
  private readonly facilityService = inject(FacilityService);

  @Input() placeholder = 'Select facility';
  @Input() searchable = true;
  @Input() clearable = true;
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() label = '';
  @Input() required = false;
  @Input() payload: Partial<FacilitySearchDto> = {};
  @Input() showAllOption = false;
  @Input() allOptionLabel = 'All';

  options: SelectOption[] = [];
  loading: SelectLoading = { selector: false, options: false };
  value: string | string[] | null = null;

  private currentPage = 1;
  private hasNextPage = false;
  private lastSearchTerm: string | null = null;
  private readonly ALL_OPTION: SelectOption = { value: 'all', label: 'All' };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | string[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.loadFacilities(1, null, false);
  }

  writeValue(value: string | string[] | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: string | string[] | null): void {
    if (this.showAllOption && this.multiple && Array.isArray(value)) {
      const allValue = this.ALL_OPTION.value;
      const previous = Array.isArray(this.value)
        ? (this.value as string[])
        : [];
      const hadAll = previous.includes(allValue);
      const hasAll = value.includes(allValue);

      if (!hadAll && hasAll) {
        // User selected "All" → clear every other selection
        value = [allValue];
      } else if (hadAll && hasAll) {
        // User selected a normal option while "All" was active → drop "All"
        value = value.filter((v) => v !== allValue);
      }
    }

    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  onSearchChange(searchTerm: string | null): void {
    this.lastSearchTerm = searchTerm;
    this.loadFacilities(1, searchTerm, false);
  }

  onScrollOptions(percent: number): void {
    if (percent < 80 || !this.hasNextPage || this.loading.options) return;
    this.loadFacilities(this.currentPage + 1, this.lastSearchTerm, true);
  }

  private loadFacilities(
    page: number,
    searchTerm: string | null,
    append: boolean
  ): void {
    this.loading = { selector: !append, options: true };

    this.facilityService
      .getAllFacilities({
        sortBy: 'name',
        sortDescending: false,
        ...this.payload,
        searchTerm: searchTerm ?? undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE
      })
      .subscribe({
        next: (response) => {
          const newOptions = this.mapToOptions(response.items);

          if (append) {
            this.options = [...this.options, ...newOptions];
          } else {
            const prefix =
              this.showAllOption && page === 1
                ? [{ ...this.ALL_OPTION, label: this.allOptionLabel }]
                : [];
            this.options = [...prefix, ...newOptions];
          }

          this.currentPage = page;
          this.hasNextPage = response.hasNextPage;
          this.loading = { selector: false, options: false };
        },
        error: () => {
          if (!append) this.options = [];
          this.loading = { selector: false, options: false };
        }
      });
  }

  private mapToOptions(facilities: FacilityDto[]): SelectOption[] {
    return facilities.map((facility) => ({
      value: facility.id,
      label: facility.name,
      description: facility.location
        ? facility.location.addressLine1 +
          ', ' +
          facility.location.city +
          ', ' +
          facility.location.stateCode
        : facility.contactEmail
    }));
  }
}
